import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { motion } from "motion/react";
import { useDispatch, useSelector } from "react-redux";
import Alert from "../Alert.js";
import UploadImage from "../../assets/images/icon.svg";
import { IoCloudDownloadOutline } from "react-icons/io5";
import {
  downloadChildrenTemplate,
  previewChildrenImport,
  importChildrenFromExcel,
} from "../../store/actions";
import { RESET_CHILDREN_IMPORT_SUCCESS } from "../../store/actions/actionTypes";

function TaskUploadWindow({ parentTaskName, parentSubject, childLabel, onClose, onImported, setFullScreenMode }) {
  const dispatch = useDispatch();
  const previewChildrenImportData = useSelector((state) => state.previewChildrenImportData);
  const previewChildrenImportError = useSelector((state) => state.previewChildrenImportError);
  const importChildrenFromExcelData = useSelector((state) => state.importChildrenFromExcelData);
  const importChildrenFromExcelError = useSelector((state) => state.importChildrenFromExcelError);
  const [step, setStep] = useState("idle"); // idle | uploading | preview | importing
  const [preview, setPreview] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [error, setError] = useState(null);
  const [fileToImport, setFileToImport] = useState(null);
  // childLabel is the singular child type this window creates: "Task", "Milestone" or "Subtask".
  const childLabelPlural = childLabel ? `${childLabel}s` : "Items";

  useEffect(() => {
    return () => {
      dispatch({ type: RESET_CHILDREN_IMPORT_SUCCESS });
    };
  }, [dispatch]);

  useEffect(() => {
    if (previewChildrenImportData) {
      const responsePayload = previewChildrenImportData?.message || previewChildrenImportData;
      setPreview(responsePayload);
      setStep("preview");
    }
  }, [previewChildrenImportData]);

  useEffect(() => {
    if (previewChildrenImportError) {
      setError("Failed to upload or validate the file. Please try again.");
      setStep("idle");
    }
  }, [previewChildrenImportError]);

  useEffect(() => {
    if (importChildrenFromExcelData) {
      const responsePayload = importChildrenFromExcelData?.message || importChildrenFromExcelData;
      if (responsePayload?.status === "Success") {
        onImported?.();
      } else if (responsePayload?.status === "Error" && responsePayload?.errors) {
        // Show structured validation errors from the import in the preview table
        setPreview(responsePayload);
        setStep("preview");
        setError(null);
      } else {
        setError("Import failed. Please try again.");
        setStep("preview");
      }
    }
  }, [importChildrenFromExcelData, onImported]);

  useEffect(() => {
    if (importChildrenFromExcelError) {
      setError("Import failed. Please try again.");
      setStep("preview");
    }
  }, [importChildrenFromExcelError]);

  const downloadTemplate = () => {
    dispatch(downloadChildrenTemplate(parentTaskName));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setStep("uploading");
    setError(null);
    setFileToImport(file);
    dispatch(previewChildrenImport(parentTaskName, file));
  };

  const handleConfirmImport = () => {
    if (!fileToImport) return;
    setStep("importing");
    setError(null);
    dispatch(importChildrenFromExcel(parentTaskName, fileToImport));
  };

const errorMap = React.useMemo(() => {
  if (!preview?.errors) return {};
  const map = {};
  preview.errors.forEach((e) => {
    map[`${e.row}-${e.field}`] = e.message;
  });
  return map;
}, [preview]);

const cellStyle = (row, field) => {
  const err = errorMap[`${row}-${field}`];
  return {
    padding: "8px",
    border: "1px solid #eee",
    backgroundColor: err ? "#fdecea" : "transparent",
    ...(err ? { cursor: "help" } : {}),
  };
};

  const handleStartOver = () => {
    setPreview(null);
    setFileUrl(null);
    setError(null);
    setStep("idle");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "white",
        width: "100%",
        zIndex: 999,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        className="gtm-sidebar-header"
        style={{
          borderBottom: "1px solid lightgrey",
          alignItems: "center",
          display: "flex",
          padding: "10px 20px",
        }}
      >
        <div
          style={{
            flex: "1",
            fontSize: "var(--sub-heading-font-size)",
            fontWeight: "var(--heading-font-weight)",
          }}
        >
          Bulk Add {childLabelPlural} under: {parentSubject}
        </div>
        <a href="#" className="closebtn" onClick={() => {
          onClose();
          setFullScreenMode(false);
        }}>
          &times;
        </a>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "30px" }}>
        {step === "idle" && (
        <div style={{ maxWidth: "700px", margin: "40px auto 0" }}>
            <div
            style={{
                backgroundColor: "#F6F8FA",
                border: "1px solid #eee",
                borderRadius: "8px",
                padding: "20px",
                marginBottom: "30px",
            }}
            >
            <div
            style={{
                fontWeight: "var(--heading-font-weight)",
                fontSize: "var(--sub-heading-font-size)",
                marginBottom: "10px",
            }}
            >
            How It Works
            </div>

            <ol
            style={{
                margin: 0,
                paddingLeft: "20px",
                fontSize: "var(--text-font-size)",
                lineHeight: "1.8",
            }}
            >

            <li>Download the Excel template below.</li>

            <li>
                Add <span style={{ fontWeight: "var(--heading-font-weight)" }}>one {childLabel}</span> per row under{" "}
                <span style={{ fontWeight: "var(--heading-font-weight)" }}>{parentSubject}</span>.
            </li>

            <li>
                <span style={{ fontWeight: "var(--heading-font-weight)" }}>Subject</span> is the only required field. All other fields are optional.
            </li>

            <li>
                If provided, <span style={{ fontWeight: "var(--heading-font-weight)" }}>Expected Start Date</span> and{" "}
                <span style={{ fontWeight: "var(--heading-font-weight)" }}>Expected End Date</span> must be in{" "}
                <code>YYYY-MM-DD</code> format (e.g., <code>2026-07-09</code>).
            </li>

            <li>
                <span style={{ fontWeight: "var(--heading-font-weight)" }}>Assignee</span> must be the email address of a user already assigned to this Business
                Initiative. Leave it blank to assign the {childLabel} later.
            </li>

            <li>
                Upload the completed template, review the preview, and click{" "}
                <span style={{ fontWeight: "var(--heading-font-weight)" }}>Confirm</span> to create the{" "}
                {childLabelPlural}.
            </li>
            </ol>
            </div>

            <div
            style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-around",
                alignItems: "center",
                gap: "20px",
                maxWidth: "700px", margin: "40px auto 0" 
            }}
            >
            <button
                className="buttonStyle"
                type="button"
                onClick={downloadTemplate}
                style={{
                color: "#0DA14B",
                fontSize: "var(--sub-heading-font-size)",
                fontWeight: "var(--heading-font-weight)",
                }}
            >
                <span style={{ marginRight: "10px", backgroundColor: "#eff7f3", borderRadius: "50%", color: "#0DA14B" , padding: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <IoCloudDownloadOutline 
                size={18}
                
                /></span>
                Download Template
            </button>

            <label
                className="upload-button"
                style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                }}
            >
                <img src={UploadImage} alt="upload" />
                <span
                style={{
                    color: "#0DA14B",
                    marginLeft: "15px",
                    fontSize: "var(--sub-heading-font-size)",
                    fontWeight: "var(--heading-font-weight)",
                }}
                >
                Upload Filled Template
                </span>
                <input
                type="file"
                accept=".xlsx"
                style={{ display: "none" }}
                onChange={handleFileUpload}
                />
            </label>
            </div>
        </div>
        )}

        {step === "uploading" && (
          <div style={{ textAlign: "center", marginTop: "60px" }}>
            Validating your file...
          </div>
        )}

        {step === "preview" && preview && (
          <div>
            {!preview.valid && (
              <div
                style={{
                  backgroundColor: "#fdecea",
                  border: "1px solid #f5c6cb",
                  borderRadius: "6px",
                  padding: "15px",
                  marginBottom: "20px",
                }}
              >
                <div style={{ fontWeight: "var(--heading-font-weight)", color: "#D32F2F", marginBottom: "8px" }}>
                  Please fix the following errors and re-upload:
                </div>
                <ul style={{ margin: 0, paddingLeft: "20px", fontSize: "var(--text-font-size)", color: "#D32F2F" }}>
                  {preview.error_messages.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            )}

            {preview.valid && (
                <div style={{ marginBottom: "15px", fontSize: "var(--text-font-size)" }}>
                    {preview.summary.total} new {preview.child_label}(s) will be created.
                </div>
            )}

            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--text-font-size)" }}>
              <thead style={{ backgroundColor: "#F6F8FA" }}>
                <tr>
                  <th style={{ padding: "8px", textAlign: "left", border: "1px solid #eee", fontWeight:"var(--heading-font-weight)" }}>Row</th>
                  <th style={{ padding: "8px", textAlign: "left", border: "1px solid #eee", fontWeight:"var(--heading-font-weight)" }}>Subject</th>
                  <th style={{ padding: "8px", textAlign: "left", border: "1px solid #eee", fontWeight:"var(--heading-font-weight)" }}>Start</th>
                  <th style={{ padding: "8px", textAlign: "left", border: "1px solid #eee", fontWeight:"var(--heading-font-weight)" }}>End</th>
                  <th style={{ padding: "8px", textAlign: "left", border: "1px solid #eee", fontWeight:"var(--heading-font-weight)" }}>Assignee</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.row}>
                    <td style={{ padding: "8px", border: "1px solid #eee" }}>{r.row}</td>
                    <td style={cellStyle(r.row, "Subject")} title={errorMap[`${r.row}-Subject`]}>
                      {r.subject}
                    </td>
                    <td style={cellStyle(r.row, "Expected Start Date")} title={errorMap[`${r.row}-Expected Start Date`]}>
                      {r.exp_start_date}
                    </td>
                    <td style={cellStyle(r.row, "Expected End Date")} title={errorMap[`${r.row}-Expected End Date`]}>
                      {r.exp_end_date}
                    </td>
                    <td style={cellStyle(r.row, "Assignee")} title={errorMap[`${r.row}-Assignee`]}>
                      {r.assignee}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
              <Button
                type="button"
                onClick={handleStartOver}
                style={{
                  backgroundColor: "white",
                  color: "gray",
                  border: "1px solid lightgray",
                }}
              >
                Start Over
              </Button>
              <Button
                type="button"
                onClick={handleConfirmImport}
                disabled={!preview.valid}
                style={{
                  backgroundColor: "var(--button-primary-color)",
                  border: "1px solid var(--button-primary-color)",
                  opacity: preview.valid ? 1 : 0.4,
                }}
              >
                Proceed & Import
              </Button>
            </div>
          </div>
        )}

        {step === "importing" && (
          <div style={{ textAlign: "center", marginTop: "60px" }}>Importing...</div>
        )}

        {error && (
          <div style={{ color: "#D32F2F", marginTop: "20px", fontSize: "var(--text-font-size)" }}>
            {error}
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default TaskUploadWindow;